import { SurveyData } from '../../services/listingsSearchCriteriaApi';

export type WizardStep = 'intro' | 'mode' | 'where' | 'budget' | 'extras' | 'review';
export type LocationMode = 'area' | 'postcode';
export type FurnishOption = 'furnished' | 'part_furnished' | 'unfurnished';

export const MAX_BEDROOMS = 5;
export const MAX_BATHROOMS = 5;
export const MAX_BUDGET = 10000;
export const BUDGET_STEP = 100;

export interface WizardState {
  step: WizardStep;
  fromReview: boolean;
  mode: LocationMode | null;
  areas: string[];
  postcodes: string[];
  bedMin: number;
  bedMax: number;
  budMin: number;
  budMax: number;
  anyDate: boolean;
  date: string;
  furnish: FurnishOption[];
  bathMin: number;
  bathMax: number;
  size: number;
  deposit: number;
}

export const initialWizardState: WizardState = {
  step: 'intro',
  fromReview: false,
  mode: null,
  areas: [],
  postcodes: [],
  bedMin: 1,
  bedMax: 2,
  budMin: 1500,
  budMax: 2800,
  anyDate: true,
  date: '',
  furnish: [],
  bathMin: 1,
  bathMax: MAX_BATHROOMS,
  size: 0,
  deposit: 0,
};

export type WizardAction =
  | { type: 'next' }
  | { type: 'back' }
  | { type: 'edit'; step: WizardStep }
  | { type: 'pickMode'; mode: LocationMode }
  | { type: 'toggleArea'; id: string }
  | { type: 'setAreas'; ids: string[] }
  | { type: 'setPostcodes'; codes: string[] }
  | { type: 'tapRange'; field: 'bed' | 'bath'; value: number }
  | { type: 'setBudgetMin'; value: number }
  | { type: 'setBudgetMax'; value: number }
  | { type: 'setBudget'; min: number; max: number }
  | { type: 'setAnyDate'; anyDate: boolean }
  | { type: 'setDate'; date: string }
  | { type: 'toggleFurnish'; option: FurnishOption }
  | { type: 'setSize'; size: number }
  | { type: 'setDeposit'; deposit: number };

const STEPS: WizardStep[] = ['intro', 'mode', 'where', 'budget', 'extras', 'review'];
const EDITABLE_FROM_REVIEW: WizardStep[] = ['where', 'budget', 'extras'];

export function progressStep(step: WizardStep): number | null {
  const index = STEPS.indexOf(step);
  return index >= 1 && index <= 4 ? index : null;
}

export function invalidHint(state: WizardState): string {
  switch (state.step) {
    case 'mode':
      return state.mode ? '' : 'Choose one to continue';
    case 'where':
      if (state.mode === 'postcode') return state.postcodes.length ? '' : 'Add at least one postcode';
      return state.areas.length ? '' : 'Add at least one neighbourhood';
    case 'budget':
      return state.budMax > state.budMin ? '' : 'Maximum budget must be higher than minimum';
    case 'extras':
      return state.anyDate || state.date ? '' : 'Pick a move-in date';
    default:
      return '';
  }
}

export function isStepValid(state: WizardState): boolean {
  return invalidHint(state) === '';
}

export function wizardReducer(state: WizardState, action: WizardAction): WizardState {
  switch (action.type) {
    case 'next': {
      if (!isStepValid(state)) return state;
      if (state.fromReview && EDITABLE_FROM_REVIEW.includes(state.step)) {
        return { ...state, step: 'review', fromReview: false };
      }
      const index = STEPS.indexOf(state.step);
      return { ...state, step: STEPS[Math.min(index + 1, STEPS.length - 1)] };
    }
    case 'back': {
      if (state.fromReview) return { ...state, step: 'review', fromReview: false };
      const index = STEPS.indexOf(state.step);
      return { ...state, step: STEPS[Math.max(index - 1, 0)] };
    }
    case 'edit':
      return { ...state, step: action.step, fromReview: EDITABLE_FROM_REVIEW.includes(action.step) };
    case 'pickMode':
      return {
        ...state,
        mode: action.mode,
        areas: action.mode === 'area' ? state.areas : [],
        postcodes: action.mode === 'postcode' ? state.postcodes : [],
      };
    case 'toggleArea':
      return {
        ...state,
        areas: state.areas.includes(action.id)
          ? state.areas.filter(id => id !== action.id)
          : [...state.areas, action.id],
      };
    case 'setAreas':
      return { ...state, areas: action.ids };
    case 'setPostcodes':
      return { ...state, postcodes: action.codes };
    case 'tapRange': {
      const [minKey, maxKey] = action.field === 'bed' ? ['bedMin', 'bedMax'] as const : ['bathMin', 'bathMax'] as const;
      const [min, max] = rangeTap(state[minKey], state[maxKey], action.value);
      return { ...state, [minKey]: min, [maxKey]: max };
    }
    case 'setBudgetMin': {
      const budMin = Math.min(action.value, MAX_BUDGET - BUDGET_STEP);
      return { ...state, budMin, budMax: Math.max(state.budMax, budMin + BUDGET_STEP) };
    }
    case 'setBudgetMax': {
      const budMax = Math.max(action.value, BUDGET_STEP);
      return { ...state, budMax, budMin: Math.min(state.budMin, budMax - BUDGET_STEP) };
    }
    case 'setBudget':
      return { ...state, budMin: action.min, budMax: action.max };
    case 'setAnyDate':
      return { ...state, anyDate: action.anyDate };
    case 'setDate':
      return { ...state, date: action.date };
    case 'toggleFurnish':
      return {
        ...state,
        furnish: state.furnish.includes(action.option)
          ? state.furnish.filter(option => option !== action.option)
          : [...state.furnish, action.option],
      };
    case 'setSize':
      return { ...state, size: Math.max(0, Math.min(500, action.size || 0)) };
    case 'setDeposit':
      return { ...state, deposit: action.deposit };
  }
}

function rangeTap(min: number, max: number, tapped: number): [number, number] {
  if (tapped < min) return [tapped, max];
  if (tapped > max) return [min, tapped];
  if (min === max) return [min, max];
  if (tapped === min) return [min + 1, max];
  if (tapped === max) return [min, max - 1];
  return tapped - min <= max - tapped ? [tapped, max] : [min, tapped];
}

export function wizardToSurveyData(state: WizardState): SurveyData {
  const data: SurveyData = {
    locations: state.mode === 'area' ? state.areas : [],
  };

  if (state.mode === 'postcode') data.postcodes = state.postcodes;
  data.min_bedrooms = state.bedMin;
  data.max_bedrooms = state.bedMax;
  if (state.budMin > 0) data.min_price = state.budMin;
  if (state.budMax < MAX_BUDGET) data.max_price = state.budMax;
  if (!state.anyDate && state.date) data.let_available_date = state.date;
  if (state.furnish.length) data.furnish_types = state.furnish;
  if (state.bathMin > 1 || state.bathMax < MAX_BATHROOMS) {
    data.min_bathrooms = state.bathMin;
    data.max_bathrooms = state.bathMax;
  }
  if (state.size) data.flat_size_min = state.size;
  if (state.deposit) data.deposit_max = state.deposit;

  return data;
}
